/**
 * New Year posters — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_seasons`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const newYearPostersPack: ToilePack = {
    slug: 'new-year-posters',
    category: 'season',
    name: 'New Year posters',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'fireworks', label: 'Fireworks', subject: 'A tall burst of fireworks over a skyline filling the poster'},
        {key: 'clock', label: 'Big clock', subject: 'A large clock face with hands at midnight and rays behind it'},
        {key: 'greeting', label: 'Greeting', subject: 'A poster with "HAPPY NEW YEAR" lettered large in bold type with a sunburst and nothing else'},
    ],
    styles: [
        {key: 'deco', label: 'Art deco', style: 'art deco illustration, geometric fans and sunbursts, clean lines, no outlines', palette: 'black and gold with a little cream', framing: 'Centered on a solid black background with an even margin'},
        {key: 'neon', label: 'Neon party', style: 'neon party graphic, glowing lines, confetti and streamers', palette: 'electric pink, cyan and gold on deep navy', framing: 'Centered on a dark background, built to read clean at small size'},
        {key: 'minimal', label: 'Minimal', style: 'modern minimal illustration, few flat shapes, generous space', palette: 'gold and white with a single black', framing: 'Centered on a plain white background with generous space around it'},
    ],
    directions: ['for a shop window', 'in black and white', 'as a smaller flyer'],
    directionHint: 'try: for a shop window',
    seo: {
        title: 'New Year Poster Design: Fireworks, Clock, Greeting',
        metaDescription: 'Nine new year poster designs — fireworks burst, big clock, lettered greeting — bold and readable from the street, in three styles.',
        h1: 'New year poster design: fireworks, clock, greeting',
        tagline: 'Nine posters, three midnight subjects, three styles each.',
        intro: 'Bold, flat window posters for the countdown: a burst of fireworks, a big clock, a lettered greeting, each built to read from across the street. Ask for a bottle-pop scene, a party address panel, or your own line, and the same three styles carry it.',
        useCases: [
            'a shop or bar window poster',
            'a house-party front door sign',
            "a printed flyer for a New Year's Eve event",
            'a classroom or office countdown display',
        ],
        faq: [
            {q: 'What does this pack actually look like?', a: 'Nine poster designs — a fireworks burst, a big clock, a lettered greeting — bold and flat, in three styles.'},
            {q: 'Will the text be readable from a distance?', a: 'Yes — the lettering is built large and high-contrast for a window or wall poster, not fine print.'},
            {q: 'Can I get my own party details on the poster?', a: 'The pack renders a general new-year greeting layout; adding your own date or address on top is a quick edit after downloading.'},
            {q: 'What size do I get, and can I print it large?', a: 'Each design renders as a single square image at print resolution — scale it up for a poster-size print or down for a flyer.'},
            {q: "Can I use this for a shop or event I'm promoting?", a: 'Yes — check the license terms on your account for the specifics of commercial use.'},
        ],
    },
}
